import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '@/store';
import { envConfig } from '@/configs';
import { useTranslation } from 'react-i18next';
import { getParsedPrice } from '@/utils/getParsedPrice';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useWalletPageContext } from './WalletPageContext';
import { useUniversityContext, useUserContext } from '@/context';
import { formatPaymentTransactionDateTime } from './paymentTransactionUtils';
import { Flex, LoadingSpin, PaymentCard, SwitchGroup, A11y, Pagination, Scrollbar, LogTable, PermissionView } from '@/components';
import TransferCredit from './TransferCredit';
import IncrementCredit from './IncrementCredit';
import LegacyIncrementCredit from './LegacyIncrementCredit';
import { useQuery } from '@tanstack/react-query';
import { PaymentHttp } from '@/services';
import type { PaymentTransactionItem } from '@/services/payment.service';
import { Typography, Tooltip } from 'antd';
import type { ColumnType } from 'antd/es/table';
import WalletTransactionListMobile from './WalletTransactionListMobile';

import './style/index.css';

const ALLOW_NEW_API = [
  'https://tst.samad.app',
  'https://ts1.samad.app',
  'https://ts2.samad.app',
  'https://ts3.samad.app',
];

const KEY_CREDIT_INC = 'CREDIT_INC';
const KEY_CREDIT_TRANSFER = 'CREDIT_TRANSFER';
const KEY_PAYMENT_HISTORY = 'PAYMENT_HISTORY';

type SlideCardKey = 'cash-credit' | 'subsidy-credit';

type TxRow = Omit<PaymentTransactionItem, 'amount'> & {
  key: string;
  successfulLabel: string;
  transactionTime: React.ReactElement;
  amount: string;
};

export function WalletPageContent() {

  const { profile, roles } = useUserContext();
  const { university } = useUniversityContext();
  const [t] = useTranslation(['common', 'payment']);
  const [selectedCard, setSelectedCard] = useState<SlideCardKey>();
  const { credit: creditValue, subsidies, subsidyCredit, loadingCredit } = useAppStore();
  const { allowIncrease, gatewayInfo, transferInfo, loading, hasSubsidy } = useWalletPageContext();
  const [selectedTab, setSelectedTab] = useState(allowIncrease ? KEY_CREDIT_INC : KEY_CREDIT_TRANSFER);
  const [page, setPage] = useState<number>(0);
  const [pageSize] = useState<number>(10);
  const [isMobileLayout, setIsMobileLayout] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const apply = () => setIsMobileLayout(mq.matches);
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    if (!isMobileLayout && selectedTab === KEY_PAYMENT_HISTORY) {
      setSelectedTab(allowIncrease ? KEY_CREDIT_INC : KEY_CREDIT_TRANSFER);
    }
  }, [isMobileLayout, selectedTab, allowIncrease]);

  const canViewOwnPayments = roles.includes('ROLE_USER_VIEW_OWN_PAYMENTS');

  const tabs = useMemo(() => {
    const arr = [];
    if (allowIncrease) {
      arr.push({ key: KEY_CREDIT_INC, name: t('payment:labels.wallet-tab-charge') });
    }

    const types = profile?.group?.transferTypes;
    if (
      types == null ||
      selectedCard === 'cash-credit' && types?.includes('CASH') ||
      selectedCard === 'subsidy-credit' && types?.includes('SUBSIDY')
    ) {
      arr.push({ key: KEY_CREDIT_TRANSFER, name: t('payment:labels.wallet-tab-transfer') });
    }
    return arr;
  }, [t, allowIncrease, selectedCard, profile]);

  const switchTabs = useMemo(() => {
    if (!isMobileLayout || !canViewOwnPayments) return tabs;
    return [...tabs, { key: KEY_PAYMENT_HISTORY, name: t('payment:labels.wallet-tab-transactions') }];
  }, [tabs, isMobileLayout, canViewOwnPayments, t]);

  const slides = useMemo(() => {
    const result: { key: SlideCardKey; component: JSX.Element }[] = [];

    const cashCreditSlide = {
      key: 'cash-credit' as const,
      component: (
        <SwiperSlide key="slide-credit">
          <PaymentCard
            cardNumber={profile.username}
            valueText={getParsedPrice(creditValue)}
            hintText={t('payment:labels.cash-credit')}
            cardOwnerText={`${profile.firstName} ${profile.lastName}`} />
        </SwiperSlide>
      )
    };

    const subsidySlide = {
      key: 'subsidy-credit' as const,
      component: (
        <SwiperSlide key="subsidy-credit">
          <PaymentCard
            hintTextColor="#fff"
            className="card-blue"
            cardNumber={profile.username}
            valueText={getParsedPrice(subsidyCredit)}
            hintText={t('payment:labels.student-subsidy')}
            cardOwnerText={`${profile.firstName} ${profile.lastName}`} />
        </SwiperSlide>
      )
    }

    if (envConfig?.ui?.onlySubsidy) {
      result.push(subsidySlide);
    }
    else {
      result.push(cashCreditSlide);
      if (hasSubsidy) {
        result.push(subsidySlide);
      }
    }

    setSelectedCard(result[0].key);
    return result;
  }, [profile, creditValue, subsidyCredit, hasSubsidy])

  const tabsContent = useMemo(() => {
    if (loading) return {};
    const useNewAPI = window.location.origin === 'https://test.samad.app' || ALLOW_NEW_API.includes(university.universityURL);
    const obj = {};
    if (allowIncrease) {
      obj[KEY_CREDIT_INC] = useNewAPI ?
        <IncrementCredit gatewayInfo={gatewayInfo} key={KEY_CREDIT_INC} /> :
        <LegacyIncrementCredit gatewayInfo={gatewayInfo} key={KEY_CREDIT_INC} />;
    }
    obj[KEY_CREDIT_TRANSFER] = (
      <TransferCredit
        key={KEY_CREDIT_TRANSFER + selectedCard}
        transferInfo={transferInfo}
        isSubsidy={selectedCard === 'subsidy-credit'}
      />
    );
    return obj;
  }, [university, allowIncrease, gatewayInfo, transferInfo, subsidies, selectedCard, loading]);

  const { data, isFetching, error } = useQuery({
    queryKey: ['/payment-transactions/current', page],
    queryFn: async () => (await PaymentHttp.paymentReport(page, pageSize)).payload,
    enabled: !isMobileLayout,
  });

  const rows: TxRow[] = useMemo(() => {
    if (!data?.content) return [];
    return data.content.map((item, i) => {
      const successfulLabel = item.successful
        ? t('common:labels.success')
        : t('common:labels.fail');
      return {
        ...item,
        key: `${item.paymentCode}-${i}`,
        successfulLabel,
        transactionTime: (
          <div className="ltr text-left">
            {formatPaymentTransactionDateTime(item.startDate)}
          </div>
        ),
        amount: getParsedPrice(item.amount),
      };
    });
  }, [data, t]);

  const columns: ColumnType<TxRow>[] = useMemo(() => [
    {
      key: 'successfulLabel',
      dataIndex: 'successfulLabel',
      title: t('common:labels.status'),
      align: 'center',
      render: (value: string, record) => {
        const cell = <span>{value}</span>;
        const reason = typeof record.reason === 'string' && record.reason.trim() ? record.reason.trim() : undefined;
        if (reason) {
          return <Tooltip title={reason}>{cell}</Tooltip>;
        }
        return cell;
      },
    },
    {
      key: 'amount',
      dataIndex: 'amount',
      title: `${t('payment:labels.amount')} (${t('common:labels.tomans')})`,
      align: 'center',
    },
    {
      key: 'transactionTime',
      dataIndex: 'transactionTime',
      title: t('payment:labels.transaction-time'),
      align: 'center',
    },
    {
      key: 'paymentCode',
      dataIndex: 'paymentCode',
      title: t('payment:labels.payment-code'),
      align: 'center',
    },
  ], [t]);

  return (
    <>
      <Flex className="relative mx-auto w-full min-w-0 max-w-full flex-col overflow-x-hidden lg:flex-row lg:items-start lg:gap-4 lg:pb-0">
        <div
          className="payment-container mx-auto w-full min-w-0 max-w-[400px] overflow-x-hidden max-lg:min-h-0 max-lg:!aspect-auto lg:shrink-0"
        >
          {loadingCredit || loading ? (
            <Flex className="w-full h-full">
              <LoadingSpin className="m-auto text-sm text-nurture-darker" />
            </Flex>
          ) :
            <>
              <Swiper
                autoHeight
                slidesPerView="auto"
                direction="horizontal"
                spaceBetween={8}
                pagination={false}
                centeredSlides={true}
                scrollbar={{ draggable: true }}
                modules={[Pagination, Scrollbar, A11y]}
                onSlideChange={(swiper) => {
                  setSelectedCard(slides[swiper.activeIndex].key);
                }}
                className={clsx(
                  { 'swiper-single': !hasSubsidy }
                )}
                style={{ width: '100%', minHeight: 175, marginTop: '0rem', marginBottom: '0.5rem' }}
              >
                {slides.map(slide => slide.component)}
              </Swiper>
              <SwitchGroup
                items={switchTabs}
                selected={selectedTab}
                onTabChange={key => setSelectedTab(key)}
              />
              {isMobileLayout && selectedTab === KEY_PAYMENT_HISTORY && canViewOwnPayments ? (
                <PermissionView required="ROLE_USER_VIEW_OWN_PAYMENTS">
                  <WalletTransactionListMobile enabled />
                </PermissionView>
              ) :
                tabsContent[selectedTab]}
            </>
          }
        </div>
        <PermissionView required="ROLE_USER_VIEW_OWN_PAYMENTS">
          {!error && !isMobileLayout &&
            <Flex
              direction="column"
              className="payment-container mt-3 aspect-auto w-full min-w-0 max-w-full flex-1 overflow-x-hidden max-lg:!aspect-auto lg:mt-0"
            >
              <Typography.Title level={4} type="success">
                {t('payment:labels.wallet-tab-transactions')}
              </Typography.Title>
              {isFetching && !data ? (
                <Flex className="w-full py-12">
                  <LoadingSpin className="m-auto text-sm text-nurture-darker" />
                </Flex>
              ) : (
                <LogTable
                  size="small"
                  rows={rows}
                  className="no-last-th-border"
                  columns={columns as any}
                  loading={isFetching}
                  scroll={{ x: 720 }}
                  pagination={{
                    total: data?.totalElements ? data?.totalElements : 0,
                    current: page + 1,
                    pageSize: 10,
                    onChange: p => setPage(p - 1),
                    showSizeChanger: false,
                  }}
                />
              )}
            </Flex>
          }
        </PermissionView>
      </Flex>
    </>
  );
}

export default WalletPageContent;
